import { useCallback, useMemo } from 'react';
import { ChatComposer, ChatDisclaimer, ChatDrawerFooter } from '@wundo/ui';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import { m } from '@/paraglide/messages.js';
import { CHAT_UPLOAD_ACCEPT } from '@/lib/chat.ts';
import type { ChatAttachments } from '@/lib/chatAttachments.ts';
import { useChatDraft } from '@/lib/chatDraft.ts';
import { audioDeviceOptions } from '@/lib/audioDeviceOptions.ts';
import { type DictationProblem, useDictation } from '@/lib/useDictation.ts';

export interface ChatPanelFooterProps {
  expanded: boolean;
  onSend: (text: string) => void;
  /** What was said out loud, to go out as it is. */
  onDictated: (text: string) => void;
  onDictationProblem: (problem: DictationProblem | null) => void;
  disabled: boolean;
  /**
   * The backend refuses a message from an impersonated session; this is only
   * manners, so nobody types one out first. Reading back is untouched — seeing
   * the conversation is what "Login as" is for.
   */
  impersonating: boolean;
  /** Changes every moment someone is about to write. */
  focusOn: string;
  attention: number;
  attachments: ChatAttachments;
}

/** The composer under the conversation, with the line that says what Wundo is. */
export function ChatPanelFooter({
  expanded,
  onSend,
  onDictated,
  onDictationProblem,
  disabled,
  impersonating,
  focusOn,
  attention,
  attachments,
}: ChatPanelFooterProps) {
  /* Words a page put in the composer for the user, unsent. */
  const draft = useChatDraft();

  /*
   * What was dictated is an ordinary message: it goes out the moment it comes
   * back, without passing through the field. Speaking is saying something, and
   * a transcript sitting in the composer waiting to be pressed would be a
   * draft nobody asked for.
   */
  const onText = useCallback(
    (text: string) => {
      onDictationProblem(null);
      onDictated(text);
    },
    [onDictationProblem, onDictated],
  );

  const dictation = useDictation({ onText, onProblem: onDictationProblem });

  /*
   * The devices as the menu lists them. A device the browser will not name yet
   * is still a device that can be chosen, so it is numbered — the same way the
   * practice conversation numbers them.
   */
  const microphones = useMemo(
    () =>
      audioDeviceOptions(dictation.microphones, (index) =>
        m.chat_dictate_microphone_unnamed({ index }),
      ).map(({ value, label }) => ({ id: value, label })),
    [dictation.microphones],
  );

  return (
    <ChatDrawerFooter expanded={expanded}>
      <ChatComposer
        placeholder={m.chat_placeholder()}
        sendLabel={m.chat_send()}
        onSend={onSend}
        disabled={disabled}
        locked={impersonating}
        focusOn={focusOn}
        draft={draft}
        attention={attention}
        attachments={attachments.pending.map((one) => ({
          id: one.localId,
          kind: one.kind,
          name: one.name,
          detail: formatFileSize(one.size),
          uploading: one.id === undefined,
        }))}
        onAttach={attachments.add}
        onRemoveAttachment={attachments.remove}
        canAttach={attachments.canAttach}
        accept={CHAT_UPLOAD_ACCEPT}
        /*
         * No microphone where there is nothing to record with, and none for an
         * impersonated session: dictating is speaking, and the backend refuses
         * that in someone else's name for the same reason it refuses the
         * message that would follow it.
         */
        dictation={
          dictation.supported && !impersonating
            ? {
                state: dictation.state,
                seconds: dictation.seconds,
                onStart: () => {
                  onDictationProblem(null);
                  dictation.start();
                },
                onStop: dictation.stop,
                onCancel: dictation.cancel,
                onOpenOptions: dictation.requestMicrophones,
                microphones,
                microphoneId: dictation.microphoneId,
                onChooseMicrophone: dictation.chooseMicrophone,
              }
            : undefined
        }
        labels={{
          attach: m.chat_attach(),
          removeAttachment: m.chat_attachment_remove(),
          dictate: m.chat_dictate(),
          dictateStop: m.chat_dictate_stop(),
          dictateSending: m.chat_dictate_sending(),
          dictateCancel: m.chat_dictate_cancel(),
          dictateMicrophone: m.chat_dictate_microphone(),
          dictateOptions: m.chat_dictate_options(),
        }}
      />
      <ChatDisclaimer>
        {impersonating ? m.chat_impersonating() : m.chat_disclaimer()}
      </ChatDisclaimer>
    </ChatDrawerFooter>
  );
}
